/**
 * @file accountActivation.js
 * @description The one place the app sends an account's "you're in" calls: the welcome email and the admin
 *   new-signup alert. They used to fire only from the browser that clicked the verification link, so a link opened
 *   on a phone (no session there) verified the account and sent nothing (Score ABA, 2026-10-25). Now they fire
 *   whenever a signed-in, active account loads the app, on any device, until the server records them as done.
 *   Since 2026-10-28 the server also sends the welcome when the account becomes verified (profiles trigger
 *   on_profile_email_verified_welcome), so a verify link opened in another browser no longer waits for a login;
 *   the welcome call here is then a no-op.
 *
 *   Server rules win: send-welcome-email and notify-admin-signup are idempotent (welcome_email_sent_at,
 *   admin_signup_notified_at) and the welcome refuses accounts older than its window. This file only mirrors
 *   those rules so the app doesn't call for nothing.
 * @module lib/accountActivation
 */

import { supabase } from "./supabase";
import { logCaughtError } from "./errorService";
import { functionErrorMessage, functionErrorStatus, reasonFromBody } from "./functionErrorMessage";

/** Mirrors WELCOME_WINDOW_DAYS in supabase/functions/_shared/welcomeWindow.ts. */
export const ACTIVATION_WINDOW_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Which activation calls are due for this profile.
 * Active = verified, or verification is off (the app stamps email_verified then). Admins and deleted, suspended or
 * old accounts get nothing.
 * @param {Object|null} profile profiles row
 * @param {{ requireEmailVerification: boolean, now?: number }} opts
 * @returns {{ welcome: boolean, adminNotify: boolean }}
 */
export function activationCallsDue(profile, { requireEmailVerification, now = Date.now() }) {
  const none = { welcome: false, adminNotify: false };
  if (!profile || !profile.id || profile.role === "admin" || profile.deleted_at || profile.suspended) return none;
  const active = profile.email_verified === true || !requireEmailVerification;
  if (!active) return none;
  const created = Date.parse(profile.created_at || "");
  if (!Number.isFinite(created) || now - created > ACTIVATION_WINDOW_DAYS * DAY_MS) return none;
  return { welcome: !profile.welcome_email_sent_at, adminNotify: !profile.admin_signup_notified_at };
}

/**
 * Fire the due calls. Never throws; a failed call is logged to Admin > Logs and retried on the next load.
 * @returns {Promise<{ welcome?: Object, adminNotify?: Object }>} the function replies (for the caller to update state)
 */
export async function runActivationCalls(due, accessToken, locale) {
  const headers = { Authorization: `Bearer ${accessToken}` };
  const call = async (fn, body) => {
    const context = `account_activation_${fn.replace(/-/g, "_")}_failed`;
    try {
      const { data, error } = await supabase.functions.invoke(fn, { headers, body });
      if (error) {
        // An expired session (401) is normal: the app retries on the next load, nothing to fix.
        if (functionErrorStatus(error) === 401) return null;
        throw new Error(await functionErrorMessage(error, `${fn} failed`));
      }
      // The functions answer 200 { ok:false, reason } for refusals they could not complete (for example no
      // admin address configured). Skips come back as ok:true, so ok:false is always a real failure.
      if (data && data.ok === false) {
        const reason = String(reasonFromBody(data) || "no reason given").slice(0, 120);
        logCaughtError(new Error(`${fn} replied not ok: ${reason}`), context, { reason });
        return data;
      }
      return data || {};
    } catch (err) {
      logCaughtError(err, context);
      return null;
    }
  };
  const out = {};
  const jobs = [];
  if (due.welcome) jobs.push(call("send-welcome-email", { locale }).then((r) => { out.welcome = r; }));
  if (due.adminNotify) jobs.push(call("notify-admin-signup", {}).then((r) => { out.adminNotify = r; }));
  await Promise.all(jobs);
  return out;
}
